import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

const waitlistSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  first_name: z.string().min(1, "Please enter your first name"),
  last_name: z.string().min(1, "Please enter your last name"),
  zip_code: z.string().min(5, "Please enter a valid zip code").max(10),
  experience_level: z.string().min(1, "Please select your experience level"),
  vehicle_type: z.string().min(1, "Please select your vehicle type"),
  features_of_interest: z.array(z.string()).min(1, "Please select at least one feature").max(3, "Please select up to 3 features"),
  referral_source: z.string().optional(),
  referred_by_code: z.string().optional(),
  adventure_style: z.string().optional(),
  age_range: z.string().optional(),
});

export type WaitlistFormData = z.infer<typeof waitlistSchema>;

interface WaitlistSignupFormProps {
  onSubmit: (data: WaitlistFormData) => Promise<void>;
  isSubmitting: boolean;
  referredByCode?: string;
}

const experienceLevels = [
  { value: "new_owner", label: "New to off-roading - Just got my first 4x4/SUV, want to explore" },
  { value: "occasional", label: "Occasional adventurer - Hit the trails a few times a year" },
  { value: "regular", label: "Regular off-roader - Monthly+ trail runs" },
  { value: "experienced", label: "Experienced enthusiast - Multiple rigs, lead group runs" },
  { value: "professional", label: "Professional/Guide - Commercial or club leader" },
];

const vehicleTypes = [
  { value: "suv", label: "SUV / Jeep (Wrangler, 4Runner, Bronco, etc.)" },
  { value: "truck", label: "Truck (Tacoma, F-150, Colorado, etc.)" },
  { value: "sxs", label: "Side-by-Side / UTV" },
  { value: "atv", label: "ATV / Quad" },
  { value: "dirt_bike", label: "Dirt Bike / Motorcycle" },
  { value: "other", label: "Other Vehicle" },
];

const featureOptions = [
  { value: "trail_discovery", label: "Trail discovery & mapping" },
  { value: "logging_tracking", label: "Logging/tracking my adventures" },
  { value: "condition_reports", label: "Trail condition reports" },
  { value: "achievements", label: "Achievements & progress tracking" },
  { value: "community_events", label: "Community & events" },
  { value: "vehicle_recommendations", label: "Vehicle-specific trail recommendations" },
  { value: "offline_maps", label: "Offline maps for remote areas" },
];

const referralSources = [
  { value: "friend", label: "Friend/word of mouth" },
  { value: "social_media", label: "Social media" },
  { value: "search", label: "Search engine" },
  { value: "youtube", label: "YouTube" },
  { value: "forum", label: "Forum/community" },
  { value: "other", label: "Other" },
];

const adventureStyles = [
  { value: "trail_runner", label: "Trail runner - Day trips, specific destinations" },
  { value: "overlander", label: "Overlander - Multi-day trips, the journey matters" },
  { value: "both", label: "Bit of both - Mix of day runs and longer adventures" },
];

const ageRanges = [
  { value: "18-24", label: "18-24" },
  { value: "25-34", label: "25-34" },
  { value: "35-44", label: "35-44" },
  { value: "45-54", label: "45-54" },
  { value: "55-64", label: "55-64" },
  { value: "65+", label: "65 or older" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const WaitlistSignupForm = ({ onSubmit, isSubmitting, referredByCode }: WaitlistSignupFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<WaitlistFormData>({
    resolver: zodResolver(waitlistSchema),
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      zip_code: "",
      experience_level: "",
      vehicle_type: "",
      features_of_interest: [],
      referral_source: "",
      referred_by_code: referredByCode || "",
      adventure_style: "",
      age_range: "",
    },
  });

  const selectedFeatures = watch("features_of_interest") || [];
  const experienceLevel = watch("experience_level");
  const vehicleType = watch("vehicle_type");
  const referralSource = watch("referral_source");
  const adventureStyle = watch("adventure_style");
  const ageRange = watch("age_range");

  const handleFeatureToggle = (feature: string) => {
    const current = selectedFeatures;
    if (current.includes(feature)) {
      setValue("features_of_interest", current.filter((f) => f !== feature));
    } else if (current.length < 3) {
      setValue("features_of_interest", [...current, feature]);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          placeholder="your@email.com"
          {...register("email")}
          className={errors.email ? "border-destructive" : ""}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      {/* First Name */}
      <div className="space-y-2">
        <Label htmlFor="first_name">First Name *</Label>
        <Input
          id="first_name"
          type="text"
          placeholder="Your first name"
          {...register("first_name")}
          className={errors.first_name ? "border-destructive" : ""}
        />
        {errors.first_name && (
          <p className="text-sm text-destructive">{errors.first_name.message}</p>
        )}
      </div>

      {/* Last Name */}
      <div className="space-y-2">
        <Label htmlFor="last_name">Last Name *</Label>
        <Input
          id="last_name"
          type="text"
          placeholder="Your last name"
          {...register("last_name")}
          className={errors.last_name ? "border-destructive" : ""}
        />
        {errors.last_name && (
          <p className="text-sm text-destructive">{errors.last_name.message}</p>
        )}
      </div>

      {/* Zip Code */}
      <div className="space-y-2">
        <Label htmlFor="zip_code">Zip Code *</Label>
        <Input
          id="zip_code"
          type="text"
          placeholder="90210"
          {...register("zip_code")}
          className={errors.zip_code ? "border-destructive" : ""}
        />
        {errors.zip_code && (
          <p className="text-sm text-destructive">{errors.zip_code.message}</p>
        )}
      </div>

      {/* Experience Level */}
      <div className="space-y-2">
        <Label>Experience Level *</Label>
        <Select
          value={experienceLevel}
          onValueChange={(value) => setValue("experience_level", value)}
        >
          <SelectTrigger className={errors.experience_level ? "border-destructive" : ""}>
            <SelectValue placeholder="Select your experience level" />
          </SelectTrigger>
          <SelectContent>
            {experienceLevels.map((level) => (
              <SelectItem key={level.value} value={level.value}>
                {level.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.experience_level && (
          <p className="text-sm text-destructive">{errors.experience_level.message}</p>
        )}
      </div>

      {/* Vehicle Type */}
      <div className="space-y-2">
        <Label>Vehicle Type *</Label>
        <Select
          value={vehicleType}
          onValueChange={(value) => setValue("vehicle_type", value)}
        >
          <SelectTrigger className={errors.vehicle_type ? "border-destructive" : ""}>
            <SelectValue placeholder="Select your vehicle type" />
          </SelectTrigger>
          <SelectContent>
            {vehicleTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.vehicle_type && (
          <p className="text-sm text-destructive">{errors.vehicle_type.message}</p>
        )}
      </div>

      {/* Features of Interest */}
      <div className="space-y-3">
        <Label className={errors.features_of_interest ? "text-destructive" : ""}>
          What features are you most excited about? * (Select up to 3)
        </Label>
        <div className="grid grid-cols-1 gap-2">
          {featureOptions.map((feature) => (
            <div key={feature.value} className="flex items-center space-x-3">
              <Checkbox
                id={feature.value}
                checked={selectedFeatures.includes(feature.value)}
                onCheckedChange={() => handleFeatureToggle(feature.value)}
                disabled={
                  !selectedFeatures.includes(feature.value) &&
                  selectedFeatures.length >= 3
                }
              />
              <label
                htmlFor={feature.value}
                className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {feature.label}
              </label>
            </div>
          ))}
        </div>
        {errors.features_of_interest && (
          <p className="text-sm text-destructive">{errors.features_of_interest.message}</p>
        )}
      </div>

      {/* Adventure Style */}
      <div className="space-y-2">
        <Label>How would you describe your adventure style?</Label>
        <Select
          value={adventureStyle}
          onValueChange={(value) => setValue("adventure_style", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select your style" />
          </SelectTrigger>
          <SelectContent>
            {adventureStyles.map((style) => (
              <SelectItem key={style.value} value={style.value}>
                {style.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Age Range */}
      <div className="space-y-2">
        <Label>What's your age range?</Label>
        <Select
          value={ageRange}
          onValueChange={(value) => setValue("age_range", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select your age range" />
          </SelectTrigger>
          <SelectContent>
            {ageRanges.map((range) => (
              <SelectItem key={range.value} value={range.value}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Referral Source */}
      <div className="space-y-2">
        <Label>How did you hear about us?</Label>
        <Select
          value={referralSource}
          onValueChange={(value) => setValue("referral_source", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {referralSources.map((source) => (
              <SelectItem key={source.value} value={source.value}>
                {source.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Hidden referred by code */}
      <input type="hidden" {...register("referred_by_code")} />

      {/* Submit Button */}
      <Button
        type="submit"
        variant="hero"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Joining...
          </>
        ) : (
          "Join the Waitlist"
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        We'll notify you when early access is available. No spam, ever.
      </p>
    </form>
  );
};

export default WaitlistSignupForm;
